import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'

type AuthMode = 'signin' | 'signup' | 'forgot' | 'reset'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    user,
    passwordRecovery,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    requestPasswordReset,
    updatePassword,
    initialize,
    clearError,
  } = useAuthStore()

  const returnTo = searchParams.get('returnTo') || '/'

  const [mode, setMode] = useState<AuthMode>('signin')
  const activeMode: AuthMode = passwordRecovery ? 'reset' : mode
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  // Initialize auth on mount.
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect only ordinary authenticated sessions.
  useEffect(() => {
    if (user && !passwordRecovery) {
      navigate(returnTo, { replace: true })
    }
  }, [user, passwordRecovery, navigate, returnTo])

  const switchMode = (nextMode: AuthMode) => {
    clearError()
    setMessage(null)
    setPassword('')
    setConfirmPassword('')
    setMode(nextMode)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    if (activeMode === 'signin') {
      await signInWithEmail(email, password)
    } else if (activeMode === 'signup') {
      if (password !== confirmPassword) {
        return
      }
      const ok = await signUpWithEmail(email, password)
      if (ok) {
        setMessage('Check your email for a confirmation link')
      }
    } else if (activeMode === 'forgot') {
      const ok = await requestPasswordReset(email)
      if (ok) {
        setMessage('Check your email for a password reset link')
      }
    } else if (activeMode === 'reset') {
      if (password !== confirmPassword) return
      const ok = await updatePassword(password)
      if (ok) {
        setPassword('')
        setConfirmPassword('')
        setMode('signin')
        setMessage('Password updated. Sign in with your new password.')
      }
    }
  }

  const isFormValid = () => {
    if (activeMode === 'reset') {
      return Boolean(password) && password === confirmPassword
    }
    if (!email) return false
    if (activeMode === 'forgot') return true
    if (!password) return false
    if (activeMode === 'signup' && password !== confirmPassword) return false
    return true
  }

  return (
    <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
      {/* Brand */}
      <div className="mb-12 text-center">
        <h1 className="text-2xl font-light tracking-tight text-foreground">factory006</h1>
        <p className="mt-2 text-sm text-muted-foreground font-light">
          {activeMode === 'reset' ? 'Choose a new password' : 'Sign in to continue'}
        </p>
      </div>

      {/* Login card */}
      <div className="w-full max-w-sm">
        {/* Error state */}
        {error && (
          <div className="mb-6 p-4 border border-destructive/20 rounded-md bg-destructive/5">
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={clearError}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Success message */}
        {message && (
          <div className="mb-6 p-4 border border-foreground/20 rounded-md bg-foreground/5">
            <p className="text-sm text-foreground">{message}</p>
          </div>
        )}

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {activeMode !== 'reset' && (
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground"
                style={{ minHeight: '44px' }}
              />
            </div>
          )}

          {activeMode !== 'forgot' && (
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete={activeMode === 'signin' ? 'current-password' : 'new-password'}
                className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground"
                style={{ minHeight: '44px' }}
              />
            </div>
          )}

          {(activeMode === 'signup' || activeMode === 'reset') && (
            <div>
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                autoComplete="new-password"
                className={`w-full px-4 py-2.5 rounded-md border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-destructive'
                    : 'border-border'
                }`}
                style={{ minHeight: '44px' }}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-destructive">Passwords do not match</p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !isFormValid()}
            className="w-full flex items-center justify-center bg-foreground text-background hover:bg-foreground/90 px-4 py-2.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          >
            <span className="text-sm font-light">
              {loading
                ? 'Please wait...'
                : activeMode === 'signin'
                  ? 'Sign in'
                  : activeMode === 'signup'
                    ? 'Create account'
                    : activeMode === 'forgot'
                      ? 'Send reset link'
                      : 'Update password'}
            </span>
          </button>
        </form>

        {/* Mode toggle links */}
        <div className="mt-4 text-center text-sm font-light text-muted-foreground">
          {activeMode === 'signin' && (
            <>
              <button
                onClick={() => switchMode('forgot')}
                className="hover:text-foreground transition-colors"
              >
                Forgot password?
              </button>
              <span className="mx-2">·</span>
              <button
                onClick={() => switchMode('signup')}
                className="hover:text-foreground transition-colors"
              >
                Create account
              </button>
            </>
          )}
          {activeMode === 'signup' && (
            <button
              onClick={() => switchMode('signin')}
              className="hover:text-foreground transition-colors"
            >
              Already have an account? Sign in
            </button>
          )}
          {activeMode === 'forgot' && (
            <button
              onClick={() => switchMode('signin')}
              className="hover:text-foreground transition-colors"
            >
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
